import { Preferences } from '@capacitor/preferences'
import { App } from '@capacitor/app'
import { usePostHog } from '@/composables/usePostHog'
import { AnalyticsEvent } from '@/constants/analyticsEvents'

const UTM_KEY = 'balance_utm_params_v1'
const DEEP_LINK_KEY = 'balance_deep_link_v1'
const APP_OPENED_KEY = 'balance_app_opened_v1'

export interface CampaignProperties {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer_url?: string
  campaign_id?: string
}

/** URL query keys persisted for campaign / attribution (single source of truth). */
export const UTM_QUERY_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
] as const satisfies ReadonlyArray<keyof CampaignProperties>

function utmQueryFieldsEqual(
  a: Partial<CampaignProperties>,
  b: Partial<CampaignProperties>,
): boolean {
  return UTM_QUERY_PARAM_KEYS.every(
    (k) => String(a[k] ?? '') === String(b[k] ?? ''),
  )
}

function prefsHasAnyUtmKey(raw: Partial<CampaignProperties>): boolean {
  return UTM_QUERY_PARAM_KEYS.some(
    (k) => raw[k] != null && String(raw[k]).length > 0,
  )
}

async function readPrefsCampaignObject(): Promise<Partial<CampaignProperties>> {
  try {
    const { value } = await Preferences.get({ key: UTM_KEY })
    if (!value) return {}
    const parsed: unknown = JSON.parse(value)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    return parsed as Partial<CampaignProperties>
  } catch {
    return {}
  }
}

/** Parse UTM params from a URL string or current browser URL */
function parseUtmParams(url?: string): Partial<CampaignProperties> {
  try {
    const href =
      url ?? (typeof window !== 'undefined' ? window.location.href : undefined)
    if (!href) return {}
    const params = new URL(href).searchParams
    const out: Partial<CampaignProperties> = {}
    for (const key of UTM_QUERY_PARAM_KEYS) {
      out[key] = params.get(key) || undefined
    }
    return out
  } catch {
    return {}
  }
}

function pickDefinedUtms(
  props: Partial<CampaignProperties>,
): Partial<CampaignProperties> {
  return Object.fromEntries(
    Object.entries(props).filter(
      ([, v]) => v !== undefined && v !== null && String(v).length > 0,
    ),
  ) as Partial<CampaignProperties>
}

/** Read a cookie by name (browser only) */
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)',
    ),
  )
  if (!match) return undefined
  const raw = match[1]
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function readCookiesCampaignObject(): Partial<CampaignProperties> {
  return Object.fromEntries(
    UTM_QUERY_PARAM_KEYS.map((key) => [key, getCookie(key)]),
  ) as Partial<CampaignProperties>
}

/** Merge prefs JSON + marketing cookies into one attribution snapshot. */
async function loadCampaignParamsFromStorage(): Promise<{
  rawPrefs: Partial<CampaignProperties>
  merged: Partial<CampaignProperties>
}> {
  const fromPrefs = await readPrefsCampaignObject()
  const fromCookies = readCookiesCampaignObject()
  const merged = pickDefinedUtms({ ...fromCookies, ...fromPrefs })
  return { rawPrefs: fromPrefs, merged }
}

/** Store UTM params from a URL string (marketing site) or current page URL (web app). Idempotent. */
export async function storeUtmParams(sourceUrl?: string): Promise<void> {
  const incoming = pickDefinedUtms(parseUtmParams(sourceUrl))
  if (Object.keys(incoming).length === 0) return
  try {
    const { rawPrefs, merged: existing } = await loadCampaignParamsFromStorage()
    const merged = pickDefinedUtms({ ...existing, ...incoming })
    if (utmQueryFieldsEqual(merged, existing) && prefsHasAnyUtmKey(rawPrefs)) {
      return
    }
    await Preferences.set({ key: UTM_KEY, value: JSON.stringify(merged) })
  } catch (error) {
    console.warn('storeUtmParams failed:', error)
  }
}

/**
 * Retrieve stored UTM params from Capacitor Preferences, falling back to
 * cross-subdomain cookies set by the marketing site.
 * Marketing site sets cookies on `.balancebudget.app` — these are readable
 * by any subdomain (app.balancebudget.app, dev.balancebudget.app, etc.).
 */
export async function getStoredUtmParams(): Promise<
  Partial<CampaignProperties>
> {
  const { merged } = await loadCampaignParamsFromStorage()
  return merged
}

/** Store the deep link URL that opened the app */
async function storeDeepLink(url: string): Promise<void> {
  try {
    await Preferences.set({ key: DEEP_LINK_KEY, value: url })
  } catch (error) {
    console.warn('storeDeepLink failed:', error)
  }
}

/** Retrieve stored deep link URL */
export async function getStoredDeepLink(): Promise<string | undefined> {
  try {
    const { value } = await Preferences.get({ key: DEEP_LINK_KEY })
    return value || undefined
  } catch {
    return undefined
  }
}

/** Merge UTM + deep link into a single campaign properties object */
export async function getCampaignProperties(): Promise<CampaignProperties> {
  const [utm, deepLink] = await Promise.all([
    getStoredUtmParams(),
    getStoredDeepLink(),
  ])
  return {
    ...utm,
    referrer_url: deepLink,
  }
}

let handleDeepLinkImpl: ((url: string) => Promise<void>) | null = null
let coldStartBootstrap: Promise<void> | null = null

async function persistColdStartCampaign(): Promise<void> {
  await storeUtmParams()
  try {
    const result = await App.getLaunchUrl()
    if (!result?.url) return
    if (handleDeepLinkImpl) await handleDeepLinkImpl(result.url)
    else await storeDeepLink(result.url)
  } catch {
    // getLaunchUrl may fail in web context — that's fine
  }
}

/**
 * Await before reading campaign props (e.g. App First Opened) so cold-start
 * launch URL is stored first.
 */
export function ensureCampaignTrackingBootstrap(): Promise<void> {
  if (!coldStartBootstrap) coldStartBootstrap = persistColdStartCampaign()
  return coldStartBootstrap
}

/**
 * Initializes campaign tracking:
 * - Captures UTM params from the current page URL (for app.balancebudget.app redirects)
 * - Stores deep link URL when app is opened via URL scheme / universal link
 * - Tracks APP_OPENED_VIA_DEEP_LINK once per install (Preferences gate)
 *
 * Safe to call on every app mount — uses Preferences to ensure single-fire.
 */
export function useCampaignTracking() {
  const { track } = usePostHog()

  async function handleDeepLink(url: string) {
    await storeDeepLink(url)
    try {
      const { value } = await Preferences.get({ key: APP_OPENED_KEY })
      if (value === '1') return
      const props = await getCampaignProperties()
      track(AnalyticsEvent.APP_OPENED_VIA_DEEP_LINK, {
        url,
        ...props,
      })
      await Preferences.set({ key: APP_OPENED_KEY, value: '1' })
    } catch (error) {
      console.warn('handleDeepLink failed:', error)
    }
  }

  function init() {
    handleDeepLinkImpl = handleDeepLink
    void ensureCampaignTrackingBootstrap()

    void App.addListener('appUrlOpen', (event) => {
      void handleDeepLink(event.url)
    })
  }

  return { init }
}
